<template>
  <div
    v-if="getQuiz"
    :class="style.wrapperCard"
    :style="{
      borderBottom: index === quizList.length - 1 ? '1px solid transparent' : ''
    }"
    @click.stop="updateQuiz(getQuiz._id)"
  >
    <div :class="style.cardQuiz">
      <SectionHeaderEditable
        v-model="renameValue"
        :edit="isRenaming"
        @save="saveRename"
        @cancel="cancelRename"
      >
        <slot></slot>
      </SectionHeaderEditable>

      <div :class="style.actions">
        <TooltipComponent position="bottom" text="Переименовать">
          <GhostButton
            :icon="renameIcon"
            :disabled="isRenaming || isCopyingQuiz"
            @click.stop="renameQuiz(getQuiz._id)"
          />
        </TooltipComponent>

        <TooltipComponent position="bottom" text="Дублировать">
          <GhostButton
            :icon="copyIcon"
            :disabled="isRenaming || isCopyingQuiz"
            @click.stop="copyQuiz(getQuiz._id)"
          />
        </TooltipComponent>

        <TooltipComponent position="bottom" text="Открыть по ссылке">
          <GhostButton
            :icon="linkIcon"
            :disabled="isRenaming"
            @click.stop="previewQuiz(getQuiz._id)"
          />
        </TooltipComponent>

        <TooltipComponent position="bottom" text="Удалить">
          <GhostButton
            :icon="trashIcon"
            :disabled="isRenaming || isCopyingQuiz"
            @click.stop="setIdDelete(getQuiz._id)"
          />
        </TooltipComponent>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import style from "./CardQuiz.module.scss";
import copyIcon from "$images/icons/button/Name=Copy, Size=16, Tone=Default.svg";
import linkIcon from "$images/icons/button/Name=Link, Size=16, Tone=Default.svg";
import trashIcon from "$images/icons/button/Name=Trash, Size=16, Tone=Default.svg";
import renameIcon from "$images/icons/button/Name=Rename, Size=16, Tone=Default.svg";
import GhostButton from "@/elements/UI/button/ButtonStyle/Default/ghost/GhostButton.vue";
import SectionHeaderEditable from "@/elements/UI/input/SectionHeaderEditable/SectionHeaderEditable.vue";
import {API} from "@/api/generated";
import {isQuizConflictResponse} from "../services";
import {QuizListItem, loudingQuizList} from "@/page/quiz/typeQuiz";
import {getBaseUrl} from "@/page/quiz/scriptQuiz";
import {save} from "@/scripts/httpService";
import TooltipComponent from "@/elements/UI/overlay/Tooltip/TooltipComponent.vue";

export default defineComponent({
  name: "QuizItem",
  components: {
    TooltipComponent,
    GhostButton,
    SectionHeaderEditable
  },
  emits: ["updateIdDelete"],
  props: {
    index: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      style,

      renameIcon,
      copyIcon,
      linkIcon,
      trashIcon,
      isRenaming: false,
      isSavingRename: false,
      isCopyingQuiz: false,
      renameValue: "",
      renameInitialSnapshot: ""
    };
  },
  methods: {
    updateQuiz(id: string) {
      if (this.isRenaming) {
        return;
      }

      localStorage.removeItem("ABTest");
      this.$router.push("/quiz/setting/" + id + "/landingPage");
    },

    async copyQuiz(id: string): Promise<void> {
      if (this.getStatusLoadingQuizList !== "success" || this.isCopyingQuiz) {
        return;
      }

      const quiz = this.quizList.find((q) => {
        return q._id == id;
      });

      if (!quiz) {
        return;
      }

      this.isCopyingQuiz = true;

      function omit<T extends object, K extends keyof T>(
        obj: T,
        keys: readonly K[]
      ): Omit<T, K> {
        const clone = {...obj};

        for (const key of keys) {
          delete clone[key];
        }

        return clone;
      }

      try {
        const newQuiz = omit(quiz, ["_id", "customUrl"]);

        newQuiz.name = this.getUniqueQuizName(
          newQuiz.name + " (копия)",
          this.quizList
        );

        const lenQuestion = newQuiz.questions.length;
        const contactsQuestion = newQuiz.questions[lenQuestion - 2];

        if (!contactsQuestion) {
          return;
        }

        let contacts = contactsQuestion.contacts;

        if (!contacts) {
          contacts = {
            name: {bool: true},
            phone: {bool: true}
          };
        }

        if (!contacts.name) {
          contacts.name = {
            bool: true
          };
        } else if (!contacts.name.bool) {
          contacts.name.bool = true;
        }

        if (!contacts.phone) {
          contacts.phone = {
            bool: true
          };
        } else if (!contacts.phone.bool) {
          contacts.phone.bool = true;
        }

        contactsQuestion.contacts = contacts;

        const res = await API.setQuiz({body: newQuiz});
        if (isQuizConflictResponse(res)) {
          throw new Error(res.message);
        }
        this.$quizStore.addQuiz(res);
      } catch (error) {
        console.error("Ошибка при копировании квиза:", error);
      } finally {
        this.isCopyingQuiz = false;
      }
    },

    getUniqueQuizName(baseName: string, list: QuizListItem[] | "await") {
      if (list == "await") {
        return baseName;
      }

      let name = baseName;
      let counter = 1;

      while (list.map((q) => q.name).includes(name)) {
        name = `${baseName} - ${counter}`;
        counter++;
      }

      return name;
    },

    setIdDelete(id: string) {
      this.$emit("updateIdDelete", id);
    },

    async previewQuiz(id: string): Promise<void> {
      const domain = (await getBaseUrl()) || "/quiz/";

      window.open(domain + id, "_blank");
    },

    renameQuiz(id: string) {
      const quiz = this.quizList.find((q) => {
        return q._id == id;
      });

      if (!quiz) {
        return;
      }

      this.renameValue = quiz.name || "";
      this.renameInitialSnapshot = JSON.stringify(quiz);
      this.isRenaming = true;
    },

    async saveRename() {
      if (!this.isRenaming || this.isSavingRename) {
        return;
      }

      const quiz = this.getQuiz;

      if (!quiz) {
        this.cancelRename();
        return;
      }

      const nextName = this.renameValue.trim();
      const oldName = quiz.name || "";

      if (!nextName) {
        this.cancelRename();
        return;
      }

      if (nextName === oldName) {
        this.cancelRename();
        return;
      }

      this.isSavingRename = true;

      const updatedQuiz = {
        ...quiz,
        name: nextName
      };

      try {
        await save("quiz", this.renameInitialSnapshot, updatedQuiz, {
          _id: quiz._id
        });

        quiz.name = nextName;
        this.cancelRename();
      } catch (error) {
        console.error("Ошибка при переименовании квиза:", error);

        this.renameValue = oldName;
        this.cancelRename();
      } finally {
        this.isSavingRename = false;
      }
    },

    cancelRename() {
      this.isRenaming = false;
      this.renameValue = "";
      this.renameInitialSnapshot = "";
    }
  },
  computed: {
    getQuiz(): QuizListItem | undefined {
      return this.quizList[this.index];
    },
    quizList(): QuizListItem[] {
      return this.$quizStore.getQuizList;
    },
    getStatusLoadingQuizList(): loudingQuizList {
      return this.$quizStore.getStatusLoadingQuizList;
    }
  }
});
</script>
